import { useEffect, useState } from "react"
import { Task, TaskStatus } from "@/schema"
import { useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"

import { useCompleteTask, useUncompleteTask, useUpdateTask } from "@/hooks/api/tasks"
import {
  getFormFieldsRequirementFromError,
  openFormFieldsRequirementDialog,
} from "@/components/projects/form-fields-requirement"
import {
  getOutcomeRequirementFromError,
  openOutcomeRequirementDialog,
} from "@/components/projects/outcome-requirements"

interface UseOptimisticTaskActionsProps {
  task: Task
  onComplete?: (task: Task) => void
  eventId?: string
}

export function useOptimisticTaskActions({ task, onComplete, eventId }: UseOptimisticTaskActionsProps) {
  const queryClient = useQueryClient()

  // Track optimistic status locally for immediate UI feedback
  const [localOptimisticStatus, setLocalOptimisticStatus] = useState<TaskStatus>(
    task.optimistic_status || task.status || TaskStatus.TODO
  )

  // Update local status when task prop changes
  useEffect(() => {
    setLocalOptimisticStatus(task.optimistic_status || task.status || TaskStatus.TODO)
  }, [task.status, task.optimistic_status])

  // Mutations
  const { mutate: updateTask, isPending: isUpdating } = useUpdateTask()
  const { mutate: completeTask, isPending: isCompleting } = useCompleteTask()
  const { mutate: uncompleteTask, isPending: isUncompleting } = useUncompleteTask()

  // Common query invalidation logic
  const invalidateQueries = () => {
    queryClient.invalidateQueries({ queryKey: ["tasks"] })
    queryClient.invalidateQueries({ queryKey: ["date-range-tasks", "date-range"] })
    queryClient.invalidateQueries({ queryKey: ["notetakerTasks"] })
    if (eventId) {
      queryClient.invalidateQueries({ queryKey: ["calendar_event", "primary", eventId] })
    }
    queryClient.invalidateQueries({ queryKey: ["meetings"] })
  }

  // Handle task completion with optimistic updates
  const handleComplete = (checked: boolean) => {
    if (!task.id) return

    const previousStatus = localOptimisticStatus
    const newStatus = checked ? TaskStatus.DONE : TaskStatus.TODO

    // Optimistic update
    setLocalOptimisticStatus(newStatus)

    if (checked) {
      completeTask(task.id, {
        onSuccess: (updatedTask) => {
          toast.success("Task completed")
          invalidateQueries()
          if (onComplete) {
            onComplete({ ...task, ...updatedTask, status: newStatus })
          }
        },
        onError: (error) => {
          const outcomeRequirement = getOutcomeRequirementFromError(error)

          if (outcomeRequirement) {
            setLocalOptimisticStatus(TaskStatus.TODO)

            openOutcomeRequirementDialog({
              requirement: outcomeRequirement,
              onConfirm: ({ outcomeId, projectComment, formResponses }) =>
                new Promise<void>((resolve, reject) => {
                  completeTask(
                    {
                      id: task.id!,
                      outcome_id: outcomeId,
                      ...(projectComment && { project_comment: projectComment }),
                      ...(formResponses && { form_responses: formResponses }),
                    },
                    {
                      onSuccess: (updatedTask) => {
                        setLocalOptimisticStatus(TaskStatus.DONE)
                        toast.success("Task completed")
                        invalidateQueries()
                        if (onComplete) {
                          onComplete({ ...task, ...updatedTask, status: TaskStatus.DONE })
                        }
                        resolve()
                      },
                      onError: (retryError) => {
                        setLocalOptimisticStatus(previousStatus)
                        reject(retryError)
                      },
                    }
                  )
                }),
            })
            return
          }

          const formFieldsRequirement = getFormFieldsRequirementFromError(error)

          if (formFieldsRequirement) {
            setLocalOptimisticStatus(TaskStatus.TODO)

            openFormFieldsRequirementDialog({
              requirement: formFieldsRequirement,
              onConfirm: (formResponses) =>
                new Promise<void>((resolve, reject) => {
                  completeTask(
                    {
                      id: task.id!,
                      form_responses: formResponses,
                    },
                    {
                      onSuccess: (updatedTask) => {
                        setLocalOptimisticStatus(TaskStatus.DONE)
                        toast.success("Task completed")
                        invalidateQueries()
                        if (onComplete) {
                          onComplete({ ...task, ...updatedTask, status: TaskStatus.DONE })
                        }
                        resolve()
                      },
                      onError: (retryError) => {
                        setLocalOptimisticStatus(previousStatus)
                        reject(retryError)
                      },
                    }
                  )
                }),
            })
            return
          }

          setLocalOptimisticStatus(previousStatus)
        },
      })

      return
    }

    uncompleteTask(task.id, {
      onSuccess: (updatedTask) => {
        toast.success("Task marked as incomplete")
        invalidateQueries()
        if (onComplete) {
          onComplete({ ...task, ...updatedTask, status: newStatus })
        }
      },
      onError: () => {
        setLocalOptimisticStatus(previousStatus)
      },
    })
  }

  // Handle date updates with optimistic updates
  const handleDateUpdate = (deadline_at?: Date | null, all_day?: boolean) => {
    if (!task.id) return

    // The updateTask mutation already has optimistic updates built in
    updateTask(
      {
        id: task.id,
        deadline_at: deadline_at ?? null,
        ...(all_day !== undefined && { all_day }),
      },
      {
        onSuccess: () => {
          toast.success(deadline_at ? "Due date updated" : "Due date cleared")
          invalidateQueries()
        },
      }
    )
  }

  // Handle assignee updates with optimistic updates
  const handleAssigneeUpdate = (assigned_to_id: string) => {
    if (!task.id) return

    // The updateTask mutation already has optimistic updates built in
    updateTask(
      {
        id: task.id,
        assigned_to_id,
      },
      {
        onSuccess: () => {
          toast.success(assigned_to_id ? "Task assigned" : "Assignee removed")
          invalidateQueries()
        },
      }
    )
  }

  return {
    localOptimisticStatus,
    handleComplete,
    handleDateUpdate,
    handleAssigneeUpdate,
    isUpdating,
    isCompleting: isCompleting || isUncompleting,
  }
}
